import { ChangeDetectionStrategy, Component, type OnInit, inject, signal, computed } from '@angular/core';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { FirebaseAuthService } from '@/cloud/services/firebase-auth.service';
import { TenantService } from '@/cloud/services/tenant.service';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { AvatarModule } from 'primeng/avatar';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import md5 from 'md5';
import { ConnectedApps } from '@/cloud/components/connected-apps/connected-apps';
import { PageHeaderComponent } from '@/shared/page-header/page-header.component';
import { RoleBadgeComponent } from '@/shared/role-badge/role-badge.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { LoadingSkeletonComponent } from '@/shared/ui/loading-skeleton/loading-skeleton.component';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';

/** System role ids indexed by the API's legacy NamespaceRole tier (Reader=0, Writer=1, Admin=2). */
const SYSTEM_ROLE_IDS = ['reader', 'writer', 'admin'];
const SYSTEM_ROLE_NAMES = new Map([
  ['reader', 'Reader'],
  ['writer', 'Writer'],
  ['admin', 'Admin']
]);

/** The role definition a permission grants. Grants made before custom roles existed carry only the tier. */
function effectiveRoleId(perm: { role: number; roleId?: string | null }): string {
  return perm.roleId || SYSTEM_ROLE_IDS[perm.role] || 'reader';
}

interface TenantPermissionRow {
  appNamespaceId: string;
  tenantName: string;
  /** Empty when it matches the name, so it is not shown twice. */
  tenantKey: string;
  roleId: string;
  roleName: string;
}

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    PasswordModule,
    AvatarModule,
    TagModule,
    DialogModule,
    MessageModule,
    ConnectedApps,
    PageHeaderComponent,
    RoleBadgeComponent,
    EmptyStateComponent,
    LoadingSkeletonComponent,
    SectionCardComponent
  ],
  templateUrl: './user-profile.html',
  styleUrl: './user-profile.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfile implements OnInit {
  private api = inject(CloudApiService);
  private firebaseAuth = inject(FirebaseAuthService);
  private tenantService = inject(TenantService);
  userSig = this.api.currentUser;

  currentPassword = signal('');
  newPassword = signal('');
  confirmPassword = signal('');
  submitting = signal(false);
  errorSig = signal<string | null>(null);
  successSig = signal<string | null>(null);
  showPasswordDialog = signal(false);

  /** Set when the tenant list could not be fetched; rows then show without tenant names. */
  private tenantsFailed = signal(false);

  // One row per tenant the user holds a role in, named from the tenant list and the role catalogue
  tenantPermissions = computed<TenantPermissionRow[]>(() => {
    const tenants = new Map(this.tenantService.tenants().map((t) => [t.id, t]));
    const roleNames = new Map((this.api.roles() ?? []).map((r) => [r.id, r.name]));
    return (this.userSig()?.permissions ?? [])
      .map((perm) => {
        const tenant = tenants.get(perm.appNamespaceId);
        const roleId = effectiveRoleId(perm);
        const tenantName = tenant?.name || 'Unknown Tenant';
        return {
          appNamespaceId: perm.appNamespaceId,
          tenantName,
          tenantKey: tenant?.key && tenant.key !== tenantName ? tenant.key : '',
          roleId,
          roleName: roleNames.get(roleId) ?? SYSTEM_ROLE_NAMES.get(roleId.toLowerCase()) ?? 'Custom role'
        };
      })
      .sort((a, b) => a.tenantName.localeCompare(b.tenantName));
  });

  // Until the tenant list arrives, show a placeholder rather than "Unknown Tenant" rows
  tenantsLoading = computed(() => this.api.namespaces() === null && !this.tenantsFailed() && (this.userSig()?.permissions?.length ?? 0) > 0);

  // Compute profile photo URL - OAuth photo takes precedence over Gravatar
  profilePhotoUrl = computed(() => {
    const user = this.userSig();

    // Use OAuth profile photo if available
    if (user?.profilePhotoUrl) {
      return user.profilePhotoUrl;
    }

    // Fall back to Gravatar if email is available
    if (!user?.email) return null;
    // Create MD5 hash of email for Gravatar using md5 library
    const hash = md5(user.email.toLowerCase().trim());
    return `https://www.gravatar.com/avatar/${hash}?s=200&d=identicon`;
  });

  /** The photo URL that failed to load, so the avatar shows initials instead. */
  private failedPhotoUrl = signal<string | null>(null);

  showPhoto = computed(() => {
    const url = this.profilePhotoUrl();
    return !!url && url !== this.failedPhotoUrl();
  });

  // Check if the profile photo is using Gravatar (not OAuth or custom photo)
  isUsingGravatar = computed(() => {
    const user = this.userSig();
    // Using Gravatar if: not OAuth user AND no custom profilePhotoUrl set
    return !this.isOAuthUser() && !user?.profilePhotoUrl;
  });

  // Check if user is using OAuth authentication (not local)
  isOAuthUser = computed(() => {
    const user = this.userSig();
    // authProvider: 0 = Local, 1 = Microsoft, 2 = Google
    return user?.authProvider !== undefined && user.authProvider !== 0;
  });

  // Get OAuth provider name for display
  oauthProviderName = computed(() => {
    const user = this.userSig();
    if (!user?.authProvider) return null;
    switch (user.authProvider) {
      case 1: return 'Microsoft';
      case 2: return 'Google';
      case 3: return 'SAML';
      default: return null;
    }
  });

  signInMethod = computed(() => (this.isOAuthUser() ? (this.oauthProviderName() ?? 'Single sign-on') : 'Email and password'));

  // Get user initials for fallback avatar
  userInitials = computed(() => {
    const user = this.userSig();
    if (!user) return '?';
    const displayName = user.displayName || user.username;
    const parts = displayName.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
  });

  ngOnInit(): void {
    this.api.getNamespaces().catch(() => this.tenantsFailed.set(true));
    // System roles are named here; custom roles need the role catalogue
    if (this.tenantPermissions().some((p) => !SYSTEM_ROLE_NAMES.has(p.roleId.toLowerCase()))) {
      this.api.getRoles().catch(() => undefined);
    }
  }

  photoFailed() {
    this.failedPhotoUrl.set(this.profilePhotoUrl());
  }

  openPasswordDialog() {
    this.currentPassword.set('');
    this.newPassword.set('');
    this.confirmPassword.set('');
    this.errorSig.set(null);
    this.successSig.set(null);
    this.showPasswordDialog.set(true);
  }

  closePasswordDialog() {
    this.showPasswordDialog.set(false);
  }

  // Check if password change is allowed (only for email/password users)
  canChangePassword = computed(() => {
    const user = this.userSig();
    // authProvider: 0 = Local (email/password), 1 = Microsoft, 2 = Google
    return user?.authProvider === 0 || user?.authProvider === undefined;
  });

  get canSubmit() {
    const cur = this.currentPassword().trim();
    const np = this.newPassword().trim();
    const cp = this.confirmPassword().trim();
    if (!cur || !np || !cp) return false;
    if (np !== cp) return false;
    if (np.length < 6) return false; // Firebase minimum is 6
    return true;
  }

  submitChange() {
    if (!this.canSubmit || this.submitting()) return;
    this.submitting.set(true);
    this.errorSig.set(null);
    this.successSig.set(null);
    this.firebaseAuth.changePassword(this.currentPassword(), this.newPassword())
      .then(() => {
        this.successSig.set('Password updated successfully');
        this.currentPassword.set('');
        this.newPassword.set('');
        this.confirmPassword.set('');
        // Close dialog after short delay to show success message
        setTimeout(() => this.showPasswordDialog.set(false), 1500);
      })
      .catch(err => {
        const msg = typeof err === 'string' ? err : (err?.message || 'Failed to change password');
        this.errorSig.set(msg);
      })
      .finally(() => this.submitting.set(false));
  }
}
